import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { BottomNavigation } from "@/components/BottomNavigation";
import { PageTransition } from "@/components/PageTransition";
import { MenuDrawer } from "@/components/MenuDrawer";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ArrowLeft, Grid3X3, Sparkles, Trash2, MessageCircle, Settings } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { it } from "date-fns/locale";
import { toast } from "sonner";

interface UserProfileData {
  user_id: string;
  username: string | null;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  messaging_enabled: boolean;
}

interface UserPost {
  id: string;
  image_url: string;
  caption: string | null;
  likes_count: number;
  comments_count: number;
  created_at: string;
}

const UserProfile = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const targetUserId = searchParams.get("id");

  const [profile, setProfile] = useState<UserProfileData | null>(null);
  const [posts, setPosts] = useState<UserPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState<UserPost | null>(null);
  const [isOwnProfile, setIsOwnProfile] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [startingChat, setStartingChat] = useState(false);

  const handleSendMessage = async () => {
    if (!targetUserId || startingChat) return;
    setStartingChat(true);
    try {
      const { data, error } = await supabase.rpc("create_chat_with_participants", {
        _is_group: false,
        _group_name: "",
        _participant_ids: [targetUserId],
      });
      if (error) throw error;
      navigate(`/messaggi?chat=${data}`);
    } catch {
      toast.error("Errore nell'avvio della chat");
    }
    setStartingChat(false);
  };

  const handleDeletePost = async (postId: string) => {
    if (deleting) return;
    setDeleting(true);
    const { error } = await supabase.from("posts").delete().eq("id", postId);
    if (error) {
      toast.error("Errore durante l'eliminazione del post");
    } else {
      setPosts(prev => prev.filter(p => p.id !== postId));
      setSelectedPost(null);
      toast.success("Post eliminato");
    }
    setDeleting(false);
  };

  useEffect(() => {
    const loadProfile = async () => {
      if (!targetUserId) { navigate("/bacheca"); return; }

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate("/auth"); return; }

      setIsOwnProfile(session.user.id === targetUserId);

      // Fetch profile
      const { data: profileData } = await supabase
        .from("profiles")
        .select("user_id, username, first_name, last_name, avatar_url, messaging_enabled")
        .eq("user_id", targetUserId)
        .maybeSingle();

      if (!profileData) { navigate("/bacheca"); return; }
      setProfile(profileData);

      // Fetch posts
      const { data: postsData } = await supabase
        .from("posts")
        .select("id, image_url, caption, likes_count, comments_count, created_at")
        .eq("user_id", targetUserId)
        .order("created_at", { ascending: false });

      setPosts((postsData || []).filter(p => p.image_url !== "shared-content"));
      setLoading(false);
    };

    loadProfile();
  }, [targetUserId, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!profile) return null;

  const displayName = profile.username || [profile.first_name, profile.last_name].filter(Boolean).join(" ") || "Utente";

  return (
    <PageTransition>
      <div className="min-h-screen bg-background pb-32">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-background/90 backdrop-blur-lg border-b border-border/50">
          <div className="container mx-auto px-4 py-3 flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full hover:bg-muted transition-all active:scale-90"
            >
              <ArrowLeft className="w-5 h-5 text-foreground" />
            </button>
            <h1 className="text-lg font-bold text-foreground truncate flex-1">
              {profile.username ? `@${profile.username}` : displayName}
            </h1>
            {isOwnProfile && (
              <button
                onClick={() => navigate("/profile")}
                className="p-2 rounded-full hover:bg-muted transition-all active:scale-90"
              >
                <Settings className="w-5 h-5 text-foreground" />
              </button>
            )}
          </div>
        </header>

        {/* Profile Info */}
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-center gap-6">
            {/* Avatar */}
            <Avatar className="w-20 h-20 ring-2 ring-primary/20">
              <AvatarImage src={profile.avatar_url || undefined} />
              <AvatarFallback className="text-2xl bg-primary/10 text-primary">
                {displayName.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>

            {/* Stats */}
            <div className="flex-1 flex justify-around">
              <div className="flex flex-col items-center">
                <span className="text-lg font-bold text-foreground">{posts.length}</span>
                <span className="text-xs text-muted-foreground">Post</span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-lg font-bold text-foreground">
                  {posts.reduce((sum, p) => sum + p.likes_count, 0)}
                </span>
                <span className="text-xs text-muted-foreground">Like totali</span>
              </div>
              <div className="flex flex-col items-center">
                <span className="text-lg font-bold text-foreground">
                  {posts.reduce((sum, p) => sum + p.comments_count, 0)}
                </span>
                <span className="text-xs text-muted-foreground">Commenti</span>
              </div>
            </div>
          </div>

          {/* Name + Message button */}
          <div className="mt-4 flex items-center justify-between">
            <div>
              {profile.first_name || profile.last_name ? (
                <p className="text-sm font-semibold text-foreground">
                  {[profile.first_name, profile.last_name].filter(Boolean).join(" ")}
                </p>
              ) : null}
            </div>
            {!isOwnProfile && profile.messaging_enabled !== false && (
              <button
                onClick={handleSendMessage}
                disabled={startingChat}
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-semibold transition-all active:scale-95 disabled:opacity-50"
              >
                <MessageCircle className="w-4 h-4" />
                {startingChat ? "..." : "Messaggio"}
              </button>
            )}
          </div>
        </div>

        {/* Divider with grid icon */}
        <div className="border-t border-border">
          <div className="flex justify-center py-2">
            <Grid3X3 className="w-5 h-5 text-foreground" />
          </div>
        </div>

        {/* Posts Grid */}
        {posts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-center px-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-primary" />
            </div>
            <p className="text-sm text-muted-foreground">
              {isOwnProfile ? "Non hai ancora pubblicato nessun post" : "Nessun post pubblicato"}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-0.5 px-0.5">
            {posts.map(post => (
              <button
                key={post.id}
                onClick={() => setSelectedPost(selectedPost?.id === post.id ? null : post)}
                className="relative aspect-square overflow-hidden bg-muted group"
              >
                <img
                  src={post.image_url}
                  alt={post.caption || "Post"}
                  className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                  loading="lazy"
                />
                {/* Hover overlay with stats */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                  <span className="text-white text-sm font-semibold flex items-center gap-1">
                    ❤️ {post.likes_count}
                  </span>
                  <span className="text-white text-sm font-semibold flex items-center gap-1">
                    💬 {post.comments_count}
                  </span>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Selected post detail (tap on mobile) */}
        {selectedPost && (
          <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setSelectedPost(null)}>
            <div className="w-full max-w-lg bg-card border border-border rounded-2xl overflow-hidden shadow-xl" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                <div className="flex items-center gap-2">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={profile.avatar_url || undefined} />
                    <AvatarFallback className="text-xs bg-primary/10 text-primary">{displayName.charAt(0).toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{displayName}</p>
                    <p className="text-[10px] text-muted-foreground">{formatDistanceToNow(new Date(selectedPost.created_at), { addSuffix: true, locale: it })}</p>
                  </div>
                </div>
                <button onClick={() => setSelectedPost(null)} className="text-sm font-medium text-primary">Chiudi</button>
              </div>
              <img src={selectedPost.image_url} alt="" className="w-full max-h-[60vh] object-contain bg-black" />
              <div className="px-4 py-3 space-y-2">
                <p className="text-sm font-semibold text-foreground">
                  ❤️ {selectedPost.likes_count} · 💬 {selectedPost.comments_count}
                </p>
                {selectedPost.caption && (
                  <p className="text-sm text-foreground">
                    <span className="font-semibold mr-1.5">{displayName}</span>{selectedPost.caption}
                  </p>
                )}
                {isOwnProfile && (
                  <button
                    onClick={() => handleDeletePost(selectedPost.id)}
                    disabled={deleting}
                    className="flex items-center gap-1.5 text-destructive text-sm font-medium mt-2 active:scale-95 transition-transform disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    {deleting ? "Eliminazione..." : "Elimina post"}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        <BottomNavigation
          activeTab="bacheca"
          onMenuClick={() => setIsMenuOpen(true)}
        />
        <MenuDrawer open={isMenuOpen} onOpenChange={setIsMenuOpen} />
      </div>
    </PageTransition>
  );
};

export default UserProfile;
